import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useAuth } from "utils/useAuth/useAuth";
import s from "./ObsIframe.module.scss";
import { refreshTokenPromise } from "utils/api";

const config = require(`../../../config/${process.env.REACT_APP_ENV}/env.json`);

const ObsIframe = ({ path }) => {
  const { refreshToken, user, token } = useAuth();

  const iframeRef = useRef(null);
  const [isInit, setIsInit] = useState(true);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isRefreshTokenPromiseResolved, setIsRefreshTokenPromiseResolved] =
    useState(!refreshTokenPromise);
  const isTokenExpired = user.exp < Date.now() / 1000;

  /**
   * Edge case handling
   * When user enters an iframe tab with an expired but refreshable token, the iframe will receive the expired token
   * Hence, make the iframe stay in loading state until the token finishes refreshing
   */
  useEffect(() => {
    const tokenPromiseHandler = async () => {
      if (isTokenExpired && !refreshTokenPromise) {
        //manually trigger the refresher if token is expired and not already refreshing
        refreshToken();
      }
      if (refreshTokenPromise) {
        await refreshTokenPromise;
      }
      setIsRefreshTokenPromiseResolved(true);
    };
    tokenPromiseHandler();
  }, []);
  const isAwaitingTokenRefresh =
    isTokenExpired || !isRefreshTokenPromiseResolved;

  const injectToken = useCallback(() => {
    const receiver = iframeRef.current.contentWindow;
    /**
     * Tiger Ng, 23rd September, 2024
     * field "isoAgent" is added to object "isoAgent" is because of OBS use the name "isoAgent" as isoAgentId instead of object itself
     */
    const sessionJsonObject = {
      isoAgent: { ...user, isoAgent: user.isoAgentId },
      token,
    };
    receiver.postMessage(
      {
        session: JSON.stringify(sessionJsonObject),
        env: "full",
        url: path,
        portalChanges: isInit,
      },
      `${config.OBS}`,
    );
  }, [user, token, path, isInit]);

  useEffect(() => {
    if (isInit || !token) return;
    injectToken();
  }, [user, token, path]);

  const IframeOnLoadHandler = () => {
    injectToken();

    setTimeout(() => {
      setIsIframeLoaded(true);
      setIsInit(false);
    }, 1500);
  };

  return (
    <div className={s.outerContainer}>
      <div className={s.innerContainer}>
        {!isAwaitingTokenRefresh && (
          <iframe
            id={`operation-online-iframe-${path}`}
            src={config.OBS + path}
            ref={iframeRef}
            onLoad={IframeOnLoadHandler}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: "4px",
              display: isIframeLoaded ? "block" : "none",
            }}
          >
            <p>Your browser does not support iframes.</p>
          </iframe>
        )}
        {(isAwaitingTokenRefresh || !isIframeLoaded) && (
          <div className={s.spinning}>
            <div className={s.spinningIcon}>
              <CircularProgress />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ObsIframe;
